
import { useEffect, useState } from "react";
import { SEO } from "@/components/SEO";
import { Helmet } from "react-helmet-async";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

export default function SitemapXML() {
  const [xmlContent, setXmlContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    // Fetch the XML content directly from the public folder
    setIsLoading(true);
    fetch('/sitemap.xml')
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.text();
      })
      .then(text => {
        // Remove any BOM characters or whitespace before the XML declaration
        const cleanedText = text.trim().replace(/^\uFEFF/, '');
        setXmlContent(cleanedText);
        setIsLoading(false);
      })
      .catch(error => {
        console.error("Error fetching sitemap XML:", error);
        setError(error.message);
        setIsLoading(false);
      });
  }, []);

  // Create a pre-formatted display for the XML content
  const displayXML = () => {
    if (xmlContent) {
      // Simple way to show XML content with proper formatting
      return (
        <pre className="bg-gray-100 p-4 overflow-auto text-xs max-h-[80vh]">
          {xmlContent}
        </pre>
      );
    }
    return null;
  };

  return (
    <main className="flex min-h-screen flex-col">
      <SEO 
        title="XML Sitemap - London Secondary Glazing" 
        description="XML Sitemap for London Secondary Glazing website." 
        canonical="https://www.londonsecondaryglazing.co.uk/sitemap.xml"
      />
      <Helmet>
        <link rel="canonical" href="/sitemap.xml" />
      </Helmet>
      
      <Navbar />
      
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-6 text-primary">XML Sitemap</h1>
          <p className="text-lg mb-8">This is the XML sitemap of our website, which helps search engines discover and index our pages.</p>
          
          {isLoading && (
            <div className="flex justify-center py-8">
              <p className="text-gray-600">Loading sitemap...</p>
            </div>
          )}
          
          {error && (
            <div className="bg-red-100 p-4 rounded mb-4">
              <p className="text-red-700">Error loading sitemap: {error}</p>
            </div>
          )}
          
          {!isLoading && !error && displayXML()}
        </div>
      </div>
      
      <Footer />
    </main>
  );
}
